import { Button, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { transportTrackMonitorService } from "@/api/transportTrackMonitorService";
import type {
    MonitorSortOrder,
    TransportTrackMonitorMapVo,
    TransportTrackMonitorPage,
    TransportTrackMonitorVo
} from "@/api/transportTrackMonitorTypes";
import { CaptureCard } from "./inTransit/CaptureCard";
import { CaptureMap } from "./inTransit/CaptureMap";
import { CaptureSideList } from "./inTransit/CaptureSideList";
import { CaptureTable } from "./inTransit/CaptureTable";
import { CAPTURE_CATEGORY_OPTIONS, type CaptureCategoryFilter } from "./inTransit/presentation";

const EMPTY_PAGE: TransportTrackMonitorPage = {
    list: [],
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPage: 0
};

interface InTransitPanelProps {
    /** 当前服务船运单编号，监管请求以此作为唯一关联条件。 */
    transportOrderShipId: string;
    /** 旧详情页用于地图船位标签的船名。 */
    shipName?: string;
}

/**
 * 旧在途监管页的 React 容器。
 * CaptureMap、CaptureSideList、CaptureCard、CaptureTable 与旧 Vue 直接子组件保持一一映射。
 */
export function InTransitPanel({ transportOrderShipId, shipName }: InTransitPanelProps) {
    const [activeView, setActiveView] = useState<"map" | "list">("map");
    const [category, setCategory] = useState<CaptureCategoryFilter>("all");
    const [sortOrder, setSortOrder] = useState<MonitorSortOrder>("DESC");
    const [pageNumber, setPageNumber] = useState(1);
    const [capturePage, setCapturePage] = useState<TransportTrackMonitorPage>(EMPTY_PAGE);
    const [mapData, setMapData] = useState<TransportTrackMonitorMapVo>();
    const [loading, setLoading] = useState(true);
    const [captureError, setCaptureError] = useState("");
    const [selectedCaptureId, setSelectedCaptureId] = useState("");

    /** 查询条件仅随运单、分类和排序变化，避免无关渲染重复请求。 */
    const query = useMemo(
        () => ({
            transportOrderShipId,
            category: category === "all" ? undefined : category,
            sortOrder
        }),
        [transportOrderShipId, category, sortOrder]
    );

    /** 按旧页当前视图分别读取地图聚合数据或分页列表。 */
    const loadCurrentView = async () => {
        setLoading(true);
        setCaptureError("");
        try {
            if (activeView === "map") {
                setMapData(await transportTrackMonitorService.getMap(transportOrderShipId, query));
            } else {
                setCapturePage(
                    await transportTrackMonitorService.paging({
                        ...query,
                        page: pageNumber,
                        size: 10
                    })
                );
            }
        } catch {
            setCaptureError("在途监管数据加载失败");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        setCaptureError("");
        const request =
            activeView === "map"
                ? transportTrackMonitorService.getMap(transportOrderShipId, query)
                : transportTrackMonitorService.paging({ ...query, page: pageNumber, size: 10 });
        void request
            .then(value => {
                if (!active) return;
                if (activeView === "map") setMapData(value as TransportTrackMonitorMapVo);
                else setCapturePage(value as TransportTrackMonitorPage);
            })
            .catch(() => active && setCaptureError("在途监管数据加载失败"))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [activeView, pageNumber, query, transportOrderShipId]);

    /** 地图接口按点位分组，侧栏仍按接口 sequence 展平。 */
    const mapCaptureRecords = useMemo(
        () =>
            mapData?.capturePointGroups
                .flatMap(group => group.items)
                .sort((left, right) => left.sequence - right.sequence)
                .map(item => item.track) ?? [],
        [mapData]
    );
    const selectedCaptureIndex = mapCaptureRecords.findIndex(record => String(record.id) === selectedCaptureId);
    const selectedCapture = mapCaptureRecords[selectedCaptureIndex];

    /** 分类或排序变化后回到首页并关闭旧抓拍卡。 */
    const resetSelection = () => {
        setPageNumber(1);
        setSelectedCaptureId("");
    };
    const toggleSortOrder = () => {
        setSortOrder(value => (value === "DESC" ? "ASC" : "DESC"));
        resetSelection();
    };
    const viewCaptureOnMap = (record: TransportTrackMonitorVo) => {
        setSelectedCaptureId(String(record.id));
        setActiveView("map");
    };

    return (
        <section className="in-transit-supervision-shell" aria-label="在途监管">
            <div className="in-transit-toolbar">
                <div className="in-transit-view-switch" role="tablist" aria-label="在途监管视图">
                    <button
                        type="button"
                        className={`in-transit-view-switch__button${activeView === "map" ? " is-active" : ""}`}
                        role="tab"
                        aria-selected={activeView === "map"}
                        onClick={() => setActiveView("map")}
                    >
                        地图
                    </button>
                    <button
                        type="button"
                        className={`in-transit-view-switch__button${activeView === "list" ? " is-active" : ""}`}
                        role="tab"
                        aria-selected={activeView === "list"}
                        onClick={() => setActiveView("list")}
                    >
                        列表
                    </button>
                </div>
                <Select
                    className="in-transit-category-select"
                    aria-label="抓拍分类"
                    value={category}
                    options={[{ label: "全部", value: "all" }, ...CAPTURE_CATEGORY_OPTIONS]}
                    onChange={(value: CaptureCategoryFilter) => {
                        setCategory(value);
                        resetSelection();
                    }}
                />
                <div className="in-transit-legends" aria-label="抓拍分类图例">
                    {CAPTURE_CATEGORY_OPTIONS.map(option => (
                        <span key={option.value} className="in-transit-legend">
                            <i style={{ backgroundColor: option.color }} />
                            {option.label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="in-transit-supervision-shell__stage">
                {captureError ? (
                    <div className="in-transit-supervision-shell__placeholder">
                        <span>{captureError}</span>
                        <Button type="link" onClick={() => void loadCurrentView()}>
                            重试
                        </Button>
                    </div>
                ) : (
                    <>
                        {activeView === "map" ? (
                            <div className="in-transit-map-view">
                                <div className="in-transit-map-view__pane">
                                    <CaptureMap
                                        records={mapCaptureRecords}
                                        map={mapData}
                                        shipName={shipName}
                                        trackId={`in-transit-${transportOrderShipId}`}
                                        selectedCaptureId={selectedCaptureId}
                                        onSelectCapture={setSelectedCaptureId}
                                    >
                                        {selectedCapture ? (
                                            <CaptureCard
                                                record={selectedCapture}
                                                index={selectedCaptureIndex}
                                                total={mapCaptureRecords.length}
                                                hasPrevious={selectedCaptureIndex > 0}
                                                hasNext={selectedCaptureIndex < mapCaptureRecords.length - 1}
                                                onClose={() => setSelectedCaptureId("")}
                                                onPrevious={() =>
                                                    setSelectedCaptureId(
                                                        String(mapCaptureRecords[selectedCaptureIndex - 1]?.id ?? "")
                                                    )
                                                }
                                                onNext={() =>
                                                    setSelectedCaptureId(
                                                        String(mapCaptureRecords[selectedCaptureIndex + 1]?.id ?? "")
                                                    )
                                                }
                                            />
                                        ) : null}
                                    </CaptureMap>
                                </div>
                                <CaptureSideList
                                    records={mapCaptureRecords}
                                    total={mapCaptureRecords.length}
                                    selectedCaptureId={selectedCaptureId}
                                    sortOrder={sortOrder}
                                    loading={loading}
                                    onSelect={setSelectedCaptureId}
                                    onToggleSort={toggleSortOrder}
                                />
                            </div>
                        ) : (
                            <CaptureTable
                                page={capturePage}
                                sortOrder={sortOrder}
                                onToggleSort={toggleSortOrder}
                                onPageChange={setPageNumber}
                                onViewOnMap={viewCaptureOnMap}
                            />
                        )}
                        {loading ? <div className="in-transit-supervision-shell__loading">加载中</div> : null}
                    </>
                )}
            </div>
        </section>
    );
}
